import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { AsyncReportType, REPORTS_DURATION_LIST } from '@app-core/constants/constants';
import { DateService } from '@app-core/services/date/date.service';
import { DataService } from '@app-core/services/data/data.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { forkJoin, Subject } from 'rxjs';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { improvedDriverListParams } from '@app-home/models/home.model';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { Store } from '@ngrx/store';
import { State, getReportsFilter } from '@app-reports/reducers';
import { UpdateReportsFilter } from '@app-reports/actions/reports-filter.actions';
import { AsyncDownloadsService } from '@app-core/services/async-downloads/async-downloads.service';
import { RequestDownloadReportBody } from '@app-core/models/core.model';
import { HttpErrorResponse } from '@angular/common/http';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';

interface FleetSafetyReportDownloadBody extends RequestDownloadReportBody {
  unit?: string;
  language?: string;
  includeInsignificantPoints?: boolean;
}

@Component({
  selector: 'app-fleet-safety-report',
  templateUrl: './fleet-safety-report.component.html',
  styleUrls: ['./fleet-safety-report.component.scss'],
})
export class FleetSafetyReportComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public loader = false;
  public durationDays = 30;
  public durationTitle: string;
  public durationList = REPORTS_DURATION_LIST;
  public safetyStats = {};
  public startDate: string;
  public endDate: string;
  public topDriversList = [];
  public requireCoachingList = [];
  public mostImprovedList = [];
  public eventDiff: any;
  public fleetStats: any;
  public eventTrend: any;
  public currentTheme = 'light';
  public fleetId: string;
  public currentMetricUnit: string;
  public currentLanguage: string;
  public isError = false;
  public downloadReportLoader = false;
  public showSuppressedPoints = false;
  public csvDownloadSuccess: boolean = false;
  private ngUnsubscribeOnChanges: Subject<void> = new Subject<void>();
  public tagIds: any[];
  public reportTags: any[];

  constructor(
    private location: Location,
    private dateService: DateService,
    public dataService: DataService,
    private reportService: ReportsService,
    private snackbarService: SnackBarService,
    public translate: TranslateService,
    private gtmService: GoogleTagManagerService,
    private store: Store<State>,
    private asyncDownloadsService: AsyncDownloadsService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit() {
    this.dataService._currentTheme.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentTheme = value;
      }
    });

    this.dataService._currentLanguage.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentLanguage = value;
      }
    });

    this.dataService._currentMetricUnit.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.currentMetricUnit = value;
        if (this.startDate && this.endDate && this.currentMetricUnit) {
          this.initiateReportApis();
        }
      }
    });

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
        if (this.startDate && this.endDate && this.currentMetricUnit) {
          this.initiateReportApis();
        }
      }
    });

    this.changeDuration(this.durationDays);
    this.store
      .select(getReportsFilter)
      .pipe(takeUntil(this.ngUnsubscribeOnChanges))
      .subscribe((reportFilter) => {
        const { tagIds } = reportFilter;
        this.tagIds = tagIds;
      });
    this.reportTags = this.tagIds.length
      ? this.tagIds.map((tagid) => ({ tagId: tagid }))
      : this.reportService?.tagIds.map((tagid) => ({ tagId: tagid }));
  }

  public navigateBack() {
    this.location.back();
  }

  public getSuppressedPointCheckValue(value: boolean) {
    this.showSuppressedPoints = value;
  }

  public initiateReportApis() {
    this.loader = true;
    this.isError = false;
    const APIS = [
      this.reportService.getFleetStats({
        startDate: this.startDate,
        endDate: this.endDate,
        fleetId: this.fleetId,
        includeEventDiff: true,
        unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
      }),
      this.reportService.getFleetDeviceStats({
        fleetId: this.fleetId,
      }),
      this.reportService.getFleetEventTrend({
        startDate: this.startDate,
        endDate: this.endDate,
        fleetId: this.fleetId,
        unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
      }),
      this.reportService.getTopDriversList({
        startDate: this.startDate,
        endDate: this.endDate,
        fleetId: this.fleetId,
        unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
        category: 'SAFE',
      }),
      this.reportService.getCoachRecommendList({
        ...(this.tagIds && this.tagIds.length ? { 'tagIds[]': this.tagIds } : {}),
        startDate: this.startDate,
        endDate: this.endDate,
        fleetId: this.fleetId,
      }),
      this.reportService.getImprovedDriverList(
        new improvedDriverListParams({
          startDate: this.startDate,
          endDate: this.endDate,
          fleetId: this.fleetId,
          unit: this.currentMetricUnit,
        })
      ),
    ];

    forkJoin(APIS)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any) => {
          const [fleetStats = {}, deviceStats = {}, trend = {}, topDrivers = {}, coachableDrivers = {}, improvedDriversRes = {}] = res;
          const { eventDiff = {} } = fleetStats.aggregate || {};
          this.safetyStats = fleetStats.aggregate || {};
          this.safetyStats = {
            ...this.safetyStats,
            assetCount: deviceStats.deviceCount,
          };
          const filteredData: { [key: string]: any } = {};
          for (const [key, value] of Object.entries(eventDiff)) {
            const obj = value as { current: number; diff: number; percent: number };
            if (!(obj.percent === 0 && obj.current === 0)) {
              filteredData[key] = obj;
            }
          }
          this.eventDiff = filteredData;
          this.fleetStats = fleetStats;
          this.eventTrend = trend.eventTrend;
          this.topDriversList = topDrivers?.topDrivers || [];
          this.requireCoachingList = coachableDrivers?.coachableDrivers ? [...coachableDrivers.coachableDrivers] : [];
          this.mostImprovedList = improvedDriversRes?.rows || [];
        },
        () => {
          this.isError = true;
        }
      );
  }

  private getStartAndEndTime(noOfDays) {
    const dateRange = this.dateService.getDateRangeInISO(noOfDays);
    const { from, to } = dateRange;
    return {
      from,
      to,
    };
  }

  public changeDuration(days: number): void {
    const { from, to } = this.getStartAndEndTime(days);
    this.startDate = from;
    this.endDate = to;
    this.durationDays = days;
    this.gtmService.changeFleetReportDownloadDurationFilter(this.durationDays);
    this.mixpanelService.track('Select Duration - Fleet Safety Report', {
      'Duration Days': this.durationDays,
    });
    this.initiateReportApis();
  }

  public downloadFleetSafetyReport() {
    this.csvDownloadSuccess = null;
    this.downloadReportLoader = true;
    const body: FleetSafetyReportDownloadBody = {
      reportType: 'FLEET_SAFETY',
      reportFormat: 'PDF',
      startDate: this.startDate,
      endDate: this.endDate,
      fleetId: this.fleetId,
      unit: this.currentMetricUnit === 'Miles' ? 'mi' : 'km',
      language: this.currentLanguage,
      includeInsignificantPoints: this.showSuppressedPoints,
      ...(this.tagIds && this.tagIds.length ? { 'tagIds[]': this.tagIds } : {}),
    };
    this.asyncDownloadsService
      .requestDownloadReport(body)
      .pipe(
        finalize(() => {
          this.downloadReportLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          this.csvDownloadSuccess = true;
        },
        (err: HttpErrorResponse) => {
          this.csvDownloadSuccess = false;
          if (err?.status === 409) {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
          } else {
            this.snackbarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        }
      );
  }

  public navigateToDownloads() {
    this.asyncDownloadsService.navigateToExportHistory(AsyncReportType.fleetSafety.key);
  }

  public scheduleFleetSafety() {
    this.mixpanelService.track('Schedule Clicked- From Overview Tab - Fleet Safety Report', {
      'Report Type': AsyncReportType.fleetSafety.key,
    });
    this.asyncDownloadsService.navigateToCreateSchedule(AsyncReportType.fleetSafety.key);
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.ngUnsubscribeOnChanges.next();
    this.ngUnsubscribeOnChanges.complete();
  }

  public tagChange(tags: any): void {
    this.store.dispatch(new UpdateReportsFilter({ tagIds: tags }));
    this.reportService.tagIds = tags;
    this.mixpanelService.track('Select Tags - Fleet Safety Portal', {
      'Tag id': tags,
    });
    this.initiateReportApis();
  }
}
